import React from "react";
import { BrowserRouter } from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import * as Sentry from "@sentry/react";

import {
  BaseAddressesProvider,
  CommonProvider,
  DeliveriesProvider,
  DeliveryProvider,
  LoadingProvider,
  MapProvider,
  ModalProvider,
  OrderProvider,
  RidersProvider,
  RoutesProvider,
  ReallocationProvider,
} from "./stores";

import Router from "./Router";

import "bootstrap/dist/css/bootstrap.min.css";

import * as mapImages from "assets/images/map/urls";

const GlobalStyle = createGlobalStyle`
  body {
    font-size: 0;
  }
  
  *, :after, :before {
    box-sizing: border-box;
  }

  .c-red {
    color: #ea2845;
  }

  .bg-red {
    background-color: #ea2845;
    color: #fff;
  }

  // marker
  .marker {
    color: #202020;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    font-size: 14px;
    position: relative;
    padding-top: 4px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 24px;
    height: 40px;
    background-image: url(${mapImages.markerDeliver});
    
    &.completed {
      background-image: url(${mapImages.markerCompleted});
    }

    &.completed-route {
      background-image: url(${mapImages.markerCompletedRoute});
    }

    &.soojione {
      background-image: url(${mapImages.markerCompletedRoute});
    }

    &.pickup {
      background-image: url(${mapImages.markerPickup});
    }

    &.pickupPlace {
      background-image: url(${mapImages.markerPickupPlace});
    }

    &.deliverWaiting {
      background-image: url(${mapImages.markerWaiting});
    }

    &.deliverPostponed {
      background-image: url(${mapImages.markerPostponed});
    }

    &.cs {
      background-image: url(${mapImages.markerCs});
    }

    &.deliverPenalty {
      background-image: url(${mapImages.markerDeliverPenalty});
    }

    &.deliver.limitedDeliveryHours, 
    &.deliverPenalty.limitedDeliveryHours {
      background-image: url(${mapImages.markerLimitedDeliveryHours});
    }

  }

  .marker-frontdoor {
    position: relative;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background-image: url(${mapImages.markerFrontdoor});
  }

  .marker-plus-wrap {
    position: absolute;
    top: 0;
    left: 26px;
    z-index: 10;
    display: flex;
    align-items: center;
    min-height: 19px;
  }

  .marker-plus {
    height: 19px;
    border-radius: 10px;
    background-color: #202020;
    color: #fff;
    line-height: 1;
    padding: 2px 7px;
    margin-right: 2px;
  }

  .marker-near {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -4px;

    &.delivery {
      width: 14px;
      height: 14px;
      border-radius: 50%;
    }
    
    &.deliveryPickup {
      background-color: #FC3F00; // orange
    }    

    &.deliveryWait {
      background-color: #FFA800; // yellow
    }
    
    &.deliveryStart {
      background-color: #14B6C1; // mint
    }

    &.deliveryPostponed {
      background-color: #4350B6; // deepblue
    }

    &.cs {
      background-color: #000000; // black
    }
  }

  .marker-icons {
    position: absolute;
    left: 25px;
    z-index: 10;
    top: 11px;
    display: flex;

    &.plus {
      top: 20px;
    }
  }

  .marker-icon {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    margin-right: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: #fff;

    &.highPrice {
      background-color: #AE8A00; 
    }

    &.time {
      background-color: #0DBF5F; 
    }

    &.rearrange {
      background-color: #8419B6; 
    }

    &.deliveryWait {
      background-color: #FFA800; // yellow
    }
    
    &.deliveryStart {
      background-color: #14B6C1; // mint
    }

    &.deliveryPostponed {
      background-color: #4350B6; // deepblue
    }
    
    &.cs {
      background-color: #000000; // black
    }

    &.translated {
      background-color: #fd1f23; 
    }
  }

  // tooltip
  .info {
    &.deliver {
      border: 2px solid rgba(128, 128, 128, 0);
    }

    &.deliver.limitedDeliveryHours {
      border: 2px solid #2bd723;
    }

    &.deliver.highPrice {
      border: 2px solid #ea2845;
    }
  }

  .info-icons {
    display: flex;
    flex-direction: row;

    .info-icon {
      width: 14px;
      height: 14px;
      border-radius: 7px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 4px;
      font-size: 10px;
      color: #fff;

      &.highPrice {
        background-color: #AE8A00; 
      }
  
      &.time {
        background-color: #0DBF5F; 
      }
  
      &.rearrange {
        background-color: #8419B6; 
      }
  
      &.deliveryWait {
        background-color: #FFA800; // yellow
      }
      
      &.deliveryStart {
        background-color: #14B6C1; // mint
      }
  
      &.deliveryPostponed {
        background-color: #4350B6; // deepblue
      }

      &.translated {
        background-color: #fd1f23; 
      }

      &.cs {
        background-color: #000000; // deepblue
      }
      
      &.disabled {
        background-color: #dedede;
      }

     
    }
  }

  .sub-info {
    display: flex;
    flex-direction: row;
    gap: 5px;
    margin-bottom: 5px;
    flex-wrap: wrap;

    .sub-info-box {
      font-size: 12px;
      padding: 1px 4px;

      &.sub-info-black {
        background-color: black;
        color: white;
      }

      &.sub-info-password {
        background-color: red;
        color: white;
      }
    }
  }

  // modal
  .modal-backdrop {
    background-color: #000;
  }

  .modal {
    .modal-dialog {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      height: 100%;
      width: 100%;
      min-width: 100%;
      margin: 0;
      flex-direction: column;
    }
  
    .modal-content {
      width: 100vw;
      border-radius: 0;
      border: none;
      flex: none;
      position: relative;
      background-color: transparent;
    }
  }

  .modal.modal-center {
    .modal-dialog {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      min-width: 100%;
      margin: 0;
      flex-direction: column;
    }

    .modal-content {
      width: 90vw;
      border-radius: 0;
      flex: none;
      padding: 0 10px;
      postion: relative;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
      background-color: #fff;

    }

    .modal-lg {
      .modal-content {
        width: 800px;
      }
    }

    .modal-header {
      .close {
        padding: 13px;

        span {
          font-size: 26px;
        }
      }
    }

    .modal-body {
      max-height: calc(100vh - 200px);
      overflow: auto;
    }
  }
  
  // toast
  .toast-background {
    border: solid 1px #202020;
    background-color: #ffffff;
    width: 80vw;
    min-height: 0;
    height: auto;
    left: 10vw;
    top: 20px;
    border-radius: 20px;
    font-size: 0;
  }
  .toast-body {
    padding: 0 10px;
    font-size: 14px;
    color: #202020;
    text-align: center;
  }
  .fancy-progress-bar {
    opacity: 0 !important;
  }
`;

const SentryErrorBoundary = Sentry.ErrorBoundary;

const AppProvider = ({ contexts, children }) =>
  contexts.reduce(
    (prev, context) =>
      React.createElement(context, {
        children: prev,
      }),
    children,
  );

const Root = () => {
  // 500번대 에러 상태 코드와 설명 매핑
  const SERVER_ERROR_TYPES = {
    500: {
      name: "internal_server_error",
      description: "Internal Server Error",
    },
    501: { name: "not_implemented", description: "Not Implemented" },
    502: { name: "bad_gateway", description: "Bad Gateway" },
    503: { name: "service_unavailable", description: "Service Unavailable" },
    504: { name: "gateway_timeout", description: "Gateway Timeout" },
    505: {
      name: "http_version_not_supported",
      description: "HTTP Version Not Supported",
    },
    506: {
      name: "variant_also_negotiates",
      description: "Variant Also Negotiates",
    },
    507: { name: "insufficient_storage", description: "Insufficient Storage" },
    508: { name: "loop_detected", description: "Loop Detected" },
    509: {
      name: "bandwidth_limit_exceeded",
      description: "Bandwidth Limit Exceeded",
    },
    510: { name: "not_extended", description: "Not Extended" },
    511: {
      name: "network_authentication_required",
      description: "Network Authentication Required",
    },
  };

  // 500번대 에러 체크 유틸리티 함수
  const is5xxError = (error) => {
    const status =
      error?.status || error?.statusCode || error?.response?.status;
    if (status && status >= 500 && status <= 599) {
      return status;
    }

    // 에러 메시지에서 상태 코드 찾기
    if (error?.message) {
      const match = error.message.match(/\b5\d{2}\b/);
      if (match) {
        return parseInt(match[0]);
      }
    }

    return null;
  };

  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => {
        const statusCode = is5xxError(error);
        return (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h1>오류가 발생했습니다</h1>
            <p>오류: {error.message}</p>
            {statusCode && <p>상태 코드: {statusCode}</p>}
            <button onClick={resetError}>다시 시도</button>
          </div>
        );
      }}
      showDialog={process.env.REACT_APP_ENV === "production"}
      beforeCapture={(scope, error) => {
        const statusCode = is5xxError(error);
        if (statusCode) {
          const errorType = SERVER_ERROR_TYPES[statusCode] || {
            name: `server_error_${statusCode}`,
          };

          scope.setTag("error_category", "5xx_error");
          scope.setTag("error_type", errorType.name);
          scope.setTag("http_status", statusCode.toString());
        }
      }}
    >
      <AppProvider
        contexts={[
          BaseAddressesProvider,
          CommonProvider,
          DeliveriesProvider,
          DeliveryProvider,
          LoadingProvider,
          MapProvider,
          ModalProvider,
          OrderProvider,
          RidersProvider,
          RoutesProvider,
          ReallocationProvider,
        ]}
      >
        <GlobalStyle />
        <BrowserRouter>
          <Router />
        </BrowserRouter>
      </AppProvider>
    </SentryErrorBoundary>
  );
};

export default Root;
